import { useState } from 'react';
import type { AccountUserRoleUser, FetchUsersByRoleResponse } from 'vopay';
import vopayApi from '@/services/api.config';

const { api } = vopayApi;

// Backend encodes the response with JSON_FORCE_OBJECT, so an empty Users list
// arrives as `{}` and a populated one arrives as `{ "0": {...}, "1": {...} }`
// instead of a real array. Normalise to a clean array here.
function normalizeUsers(raw: unknown): AccountUserRoleUser[] {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw as AccountUserRoleUser[];
  return Object.values(raw as Record<string, AccountUserRoleUser>);
}

/**
 * Direct Pattern hook for fetching the users assigned to a specific Role.
 * Hits GET /account/user-roles/role?RoleID=<n>.
 */
export default function useFetchUsersByRole() {
  const [fetching, setFetching] = useState(false);
  const [users, setUsers] = useState<AccountUserRoleUser[]>([]);

  async function fetchUsersByRole(roleId: number): Promise<FetchUsersByRoleResponse> {
    try {
      setFetching(true);
      const res = await api.fetchUsersByRole({ RoleID: roleId });
      if (!res.Success) {
        throw new Error(res.ErrorMessage);
      }
      setUsers(normalizeUsers(res.Users));
      return res;
    } catch (error) {
      console.error('Fetch users by role failed:', error);
      throw error;
    } finally {
      setFetching(false);
    }
  }

  function reset() {
    setUsers([]);
  }

  return { fetching, users, fetchUsersByRole, reset };
}
